// Report export: branded PDF (jsPDF) + Excel (xlsx).
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const NAVY = [10, 22, 40];
const COBALT = [0, 71, 255];
const GREEN = [22, 163, 74];
const MUT = [113, 113, 122];

// load an image URL -> dataURL (for logos)
async function toDataUrl(url) {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.readAsDataURL(blob);
    });
  } catch { return null; }
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

export async function exportReportPDF(data, opts = {}) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const m = data.meta || {};
  const instName = m.institute?.name || (m.scope === "platform" ? "All Institutes" : "Institute");

  // ---- header band ----
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, W, 88, "F");

  const arv = await toDataUrl("/brand/arventiq-full.png");
  const mark = await toDataUrl(m.institute?.logo_url || "/brand/solution-mark.png");
  if (mark) { try { doc.addImage(mark, "PNG", 32, 20, 40, 40); } catch {} }
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold"); doc.setFontSize(15);
  doc.text(instName, 82, 38, { maxWidth: W - 250 });
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(200, 210, 225);
  doc.text("IT Service Desk · Performance Report", 82, 54);
  if (arv) { try { doc.addImage(arv, "PNG", W - 150, 26, 118, 30); } catch {} }
  doc.setFontSize(7); doc.setTextColor(180, 190, 205);
  doc.text("POWERED BY", W - 150, 20);

  // ---- meta strip ----
  let y = 108;
  doc.setTextColor(...MUT); doc.setFontSize(8.5);
  const metaLine = `Period: ${fmtDate(m.from)} – ${fmtDate(m.to)}    |    Scope: ${m.technician || (m.scope === "platform" ? "Platform-wide" : "All technicians")}    |    Generated: ${fmtDate(m.generated_at)}    |    By: ${m.generated_by || "—"}`;
  doc.text(metaLine, 32, y);
  y += 8;
  doc.setDrawColor(230); doc.line(32, y, W - 32, y);
  y += 20;

  // ---- KPI row ----
  const k = data.kpis || {};
  const kpis = [
    ["Total tickets", String(k.total ?? 0)],
    ["SLA compliance", (k.slaCompliance ?? 0) + "%"],
    ["Avg resolution", k.avgResolveHrs != null ? k.avgResolveHrs + "h" : "—"],
    ["Resolution rate", (k.resolutionRate ?? 0) + "%"],
  ];
  const kw = (W - 64) / 4;
  kpis.forEach(([label, val], i) => {
    const x = 32 + i * kw;
    doc.setDrawColor(235); doc.roundedRect(x, y, kw - 10, 54, 6, 6, "S");
    doc.setTextColor(10, 10, 10); doc.setFont("helvetica", "bold"); doc.setFontSize(20);
    doc.text(val, x + 12, y + 28);
    doc.setTextColor(...MUT); doc.setFont("helvetica", "normal"); doc.setFontSize(8);
    doc.text(label, x + 12, y + 44);
  });
  y += 78;

  // ---- breakdown tables (status / category / technician) ----
  const section = (title, rows, head) => {
    doc.setTextColor(...COBALT); doc.setFont("helvetica", "bold"); doc.setFontSize(9);
    doc.text(title.toUpperCase(), 32, y);
    y += 6;
    autoTable(doc, {
      startY: y, head: [head], body: rows,
      theme: "grid", styles: { fontSize: 8.5, cellPadding: 4 },
      headStyles: { fillColor: [244, 244, 248], textColor: [90, 90, 100], fontStyle: "bold" },
      margin: { left: 32, right: 32 },
    });
    y = doc.lastAutoTable.finalY + 18;
  };

  section("Tickets by status", (data.byStatus || []).map((r) => [r.name, String(r.value)]), ["Status", "Count"]);
  if (y > 640) { doc.addPage(); y = 40; }
  section("Tickets by category", (data.byCategory || []).map((r) => [r.name, String(r.value)]), ["Category", "Count"]);
  if (y > 600) { doc.addPage(); y = 40; }
  section("Technician performance", (data.byTechnician || []).map((r) => [r.name, String(r.total), String(r.resolved)]), ["Technician", "Assigned", "Resolved"]);

  // ---- ticket detail (new page) ----
  doc.addPage(); y = 40;
  doc.setTextColor(...COBALT); doc.setFont("helvetica", "bold"); doc.setFontSize(9);
  doc.text("TICKET DETAILS", 32, y); y += 6;
  autoTable(doc, {
    startY: y,
    head: [["Ticket", "Title", "Requester", "Category", "Priority", "Status", "Assignee", "Raised", "Resolved"]],
    body: (data.tickets || []).map((t) => [
      t.ticket_no, t.title, t.requester || "—", t.category || "—",
      t.priority, t.status, t.assignee, fmtDate(t.created_at), fmtDate(t.resolved_at),
    ]),
    theme: "striped", styles: { fontSize: 7, cellPadding: 3, overflow: "linebreak" },
    headStyles: { fillColor: NAVY, textColor: [255, 255, 255], fontSize: 7 },
    columnStyles: { 1: { cellWidth: 90 } },
    margin: { left: 24, right: 24 },
  });

  // ---- footer on every page ----
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    const H = doc.internal.pageSize.getHeight();
    doc.setDrawColor(235); doc.line(32, H - 30, W - 32, H - 30);
    doc.setTextColor(...MUT); doc.setFontSize(7.5); doc.setFont("helvetica", "normal");
    doc.text("Solution · IT Service Desk by Arventiq Labs", 32, H - 16);
    doc.text(`Confidential · ${instName} · Page ${i} of ${pages}`, W - 32, H - 16, { align: "right" });
  }

  const fname = `Solution-Report-${(m.institute?.code || "platform")}-${m.from}_to_${m.to}.pdf`;
  doc.save(fname);
}

export function exportReportExcel(data) {
  const m = data.meta || {};
  const wb = XLSX.utils.book_new();

  // Summary sheet
  const k = data.kpis || {};
  const summary = [
    ["Solution — IT Service Desk Report"],
    ["Institute", m.institute?.name || "All institutes"],
    ["Period", `${m.from} to ${m.to}`],
    ["Scope", m.technician || (m.scope === "platform" ? "Platform-wide" : "All technicians")],
    ["Generated", new Date(m.generated_at).toLocaleString()],
    ["Generated by", m.generated_by || ""],
    [],
    ["KPI", "Value"],
    ["Total tickets", k.total ?? 0],
    ["Resolved", k.resolved ?? 0],
    ["Resolution rate (%)", k.resolutionRate ?? 0],
    ["SLA compliance (%)", k.slaCompliance ?? 0],
    ["Avg resolution (hrs)", k.avgResolveHrs ?? ""],
    [],
    ["Powered by Arventiq Labs"],
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), "Summary");

  // Breakdown sheet
  const breakdown = [["By status", "", "", "By category", "", "", "By technician"]];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
    (data.byCategory || []).map((c) => ({ Category: c.name, Tickets: c.value }))
  ), "By Category");
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
    (data.byTechnician || []).map((t) => ({ Technician: t.name, Assigned: t.total, Resolved: t.resolved }))
  ), "By Technician");

  // Ticket detail sheet
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
    (data.tickets || []).map((t) => ({
      Ticket: t.ticket_no, Title: t.title, Requester: t.requester, Category: t.category,
      Priority: t.priority, Status: t.status, Assignee: t.assignee,
      Raised: t.created_at ? new Date(t.created_at).toLocaleDateString() : "",
      Resolved: t.resolved_at ? new Date(t.resolved_at).toLocaleDateString() : "",
    }))
  ), "Tickets");

  XLSX.writeFile(wb, `Solution-Report-${m.institute?.code || "platform"}-${m.from}_to_${m.to}.xlsx`);
}

export function exportReportCSV(data) {
  const rows = (data.tickets || []).map((t) => ({
    Ticket: t.ticket_no, Title: t.title, Requester: t.requester, Category: t.category,
    Priority: t.priority, Status: t.status, Assignee: t.assignee,
    Raised: t.created_at, Resolved: t.resolved_at || "",
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  const csv = XLSX.utils.sheet_to_csv(ws);
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Solution-Tickets-${data.meta?.from}_to_${data.meta?.to}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
